import type { IntlShape } from 'react-intl'
import { z } from 'zod'

type BooleanOption = { value: boolean; message?: string }
type NumberOption = { value: number; message?: string }

export const numberField = (
	intl: IntlShape,
	options: {
		required?: BooleanOption
		allowNegative?: BooleanOption
		max?: NumberOption
		integer?: BooleanOption
		min?: NumberOption
	},
) => {
	const {
		required = { value: true },
		allowNegative = { value: true },
		max,
		integer = { value: false },
		min,
	} = options

	let zodNumBase = z.number({
		required_error:
			required.message ||
			intl.formatMessage({
				defaultMessage: 'Kenttä vaaditaan.',
				description: 'Error message for required number field',
			}),
		invalid_type_error: intl.formatMessage({
			defaultMessage: 'Arvon on oltava numero.',
			description: 'Error message for invalid field type',
		}),
	})

	if (!allowNegative.value) {
		zodNumBase = zodNumBase.min(0, {
			message:
				allowNegative.message ||
				intl.formatMessage({
					defaultMessage: 'Arvon on oltava nolla tai suurempi.',
					description: 'Error message for non-negative numbers',
				}),
		})
	}

	if (min && min.value !== undefined) {
		zodNumBase = zodNumBase.min(min.value, {
			message:
				min.message ||
				intl.formatMessage(
					{
						defaultMessage: 'Arvon on oltava vähintään {minAmount}',
						description: 'Error message for number below minimum',
					},
					{
						minAmount: min.value,
					},
				),
		})
	}

	if (max && max.value !== undefined) {
		zodNumBase = zodNumBase.max(max.value, {
			message:
				max.message ||
				intl.formatMessage(
					{
						defaultMessage:
							'Arvon on oltava pienempi tai yhtäsuuri kuin {maxAmount}',
						description: 'Error message for number exceeding maximum',
					},
					{
						maxAmount: max.value,
					},
				),
		})
	}

	if (integer.value) {
		zodNumBase = zodNumBase.int({
			message:
				integer.message ||
				intl.formatMessage({
					defaultMessage: 'Arvon on oltava kokonaisluku.',
					description: 'Error message for integer required',
				}),
		})
	}

	const zodNum = required.value ? zodNumBase : zodNumBase.optional()

	return z.preprocess((val) => {
		if (val === '') return undefined
		if (typeof val === 'string') {
			const lastCharacter = val.split('').pop()
			if (lastCharacter === '.') {
				return val
			} else {
				const num = Number(val)
				return isNaN(num) ? val : num
			}
		}
		return val
	}, zodNum)
}
